import { useState, useEffect } from 'react';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SEO JSON-LD Structured Data
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ivanti.tr';
    
    // Professional Service Schema
    const professionalServiceSchema = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "ivanti.tr",
      "description": "Ivanti çözümleri için Türkiye'de lisans, kurulum, danışmanlık ve destek hizmetleri",
      "url": siteUrl,
      "telephone": "+90-212-000-0000",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "İstanbul",
        "addressCountry": "TR"
      },
      "areaServed": {
        "@type": "Country",
        "name": "Türkiye"
      },
      "serviceType": ["IT Danışmanlık", "Yazılım Kurulum", "Teknik Destek", "Lisanslama"]
    };

    // FAQ Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Ivanti UEM nedir?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ivanti Unified Endpoint Management (UEM), tüm uç noktalarınızı tek platformdan yönetmenizi sağlayan kapsamlı bir çözümdür. Masaüstü, dizüstü ve mobil cihazların yönetimi, yazılım dağıtımı ve uzaktan destek özelliklerini içerir."
          }
        },
        {
          "@type": "Question",
          "name": "Ivanti ITSM kurulumu ne kadar sürer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ivanti ITSM kurulumu, projenin kapsamına bağlı olarak genellikle 4-8 hafta arasında tamamlanır. Uzman ekibimiz, sorunsuz bir geçiş süreci için yanınızda olur."
          }
        },
        {
          "@type": "Question",
          "name": "Ivanti Türkiye partner desteği nasıl alınır?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "ivanti.tr olarak Türkiye'de yerel dilde teknik destek, kurulum, eğitim ve danışmanlık hizmetleri sunuyoruz. Demo talep formunu doldurarak 24 saat içinde size dönüş yapıyoruz."
          }
        }
      ]
    };

    // WebPage Schema
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Ivanti Türkiye | UEM, ITSM, ITAM, Patch Yönetimi",
      "description": "Ivanti çözümleri için Türkiye'de lisans, kurulum, danışmanlık ve destek hizmetleri. UEM, ITSM, ITAM ve patch yönetimi için hemen demo talep edin.",
      "url": siteUrl,
      "inLanguage": "tr-TR",
      "isPartOf": {
        "@type": "WebSite",
        "name": "ivanti.tr",
        "url": siteUrl
      }
    };

    // Create and inject script elements
    const schemas = [professionalServiceSchema, faqSchema, webPageSchema];
    const scriptElements: HTMLScriptElement[] = [];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `seo-schema-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptElements.push(script);
    });

    // Cleanup on unmount
    return () => {
      scriptElements.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4evs64a42c0argjm9jg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', phone: '', interest: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold">
                <a href="/" className={`${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                  <span className="font-pacifico" style={{ fontFamily: '"Pacifico", serif' }}>ivanti</span>
                  <span className="text-[#E31C79]">.tr</span>
                </a>
              </h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollToSection('cozumler')} className={`cursor-pointer hover:text-[#E31C79] transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Çözümler</button>
              <button onClick={() => scrollToSection('senaryolar')} className={`cursor-pointer hover:text-[#E31C79] transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Kullanım Senaryoları</button>
              <button onClick={() => scrollToSection('neden-biz')} className={`cursor-pointer hover:text-[#E31C79] transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Neden Biz?</button>
              <button onClick={() => scrollToSection('iletisim')} className={`cursor-pointer hover:text-[#E31C79] transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`}>İletişim</button>
              <button onClick={() => scrollToSection('iletisim')} className="bg-[#E31C79] hover:bg-[#C01865] text-white px-6 py-2.5 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl">
                Demo Talep Et
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden cursor-pointer"
            >
              <div className={`w-6 h-6 flex items-center justify-center ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('cozumler')} className="block w-full text-left text-gray-700 hover:text-[#E31C79] py-2 cursor-pointer">Çözümler</button>
              <button onClick={() => scrollToSection('senaryolar')} className="block w-full text-left text-gray-700 hover:text-[#E31C79] py-2 cursor-pointer">Kullanım Senaryoları</button>
              <button onClick={() => scrollToSection('neden-biz')} className="block w-full text-left text-gray-700 hover:text-[#E31C79] py-2 cursor-pointer">Neden Biz?</button>
              <button onClick={() => scrollToSection('iletisim')} className="block w-full text-left text-gray-700 hover:text-[#E31C79] py-2 cursor-pointer">İletişim</button>
              <button onClick={() => scrollToSection('iletisim')} className="w-full bg-[#E31C79] text-white px-6 py-3 rounded-lg font-semibold cursor-pointer whitespace-nowrap">
                Demo Talep Et
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#2B1B3D]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#2B1B3D] via-[#3D2555] to-[#2B1B3D]"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-64 h-64 bg-[#FF6B35] rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#E31C79] rounded-full blur-3xl"></div>
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="max-w-3xl">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Ivanti Çözümleri ile <span className="text-[#FF6B35]">IT Operasyonlarınızı</span> Otomatikleştirin
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Uç nokta yönetimi, güvenlik, ITSM, ITAM ve patch otomasyonu gibi <strong>Ivanti çözümleri</strong> için Türkiye'de danışmanlık, kurulum ve destek sunuyoruz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => scrollToSection('iletisim')}
                className="bg-[#E31C79] hover:bg-[#C01865] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all cursor-pointer whitespace-nowrap shadow-xl hover:shadow-2xl hover:scale-105"
              >
                Demo Talep Et
              </button>
              <button 
                onClick={() => scrollToSection('iletisim')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-lg font-semibold text-lg transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                İhtiyaç Analizi Al
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-8 flex items-center justify-center text-white/60">
            <i className="ri-arrow-down-line text-2xl"></i>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ivanti'nin Değer Önerisi
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Kurumsal IT altyapınızı tek platformda yönetin, güvenliğinizi artırın ve operasyonel verimliliği maksimize edin.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#E31C79] transition-colors">
                <i className="ri-eye-line text-3xl text-[#E31C79] group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Uçtan Uca Görünürlük</h3>
              <p className="text-gray-600 mb-6">
                Tüm cihazları, yazılımları ve varlıkları keşfedin. Ağınızdaki her noktayı gerçek zamanlı izleyin ve yönetin.
              </p>
              <button onClick={() => scrollToSection('cozumler')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                Daha fazla bilgi
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* Card 2 */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#E31C79] transition-colors">
                <i className="ri-shield-check-line text-3xl text-[#E31C79] group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Otomatik Güvenlik</h3>
              <p className="text-gray-600 mb-6">
                Patch yönetimi, zafiyet yönetimi ve güvenli erişim ile siber tehditlere karşı proaktif koruma sağlayın.
              </p>
              <button onClick={() => scrollToSection('cozumler')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                Daha fazla bilgi
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* Card 3 */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#E31C79] transition-colors">
                <i className="ri-settings-3-line text-3xl text-[#E31C79] group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Modern IT Servisleri</h3>
              <p className="text-gray-600 mb-6">
                ITSM + ITAM + otomasyon ile tek platformda IT servis yönetimini modernize edin ve verimliliği artırın.
              </p>
              <button onClick={() => scrollToSection('cozumler')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                Daha fazla bilgi
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="cozumler" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ivanti Çözüm Kategorileri
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Kurumsal ihtiyaçlarınıza özel Ivanti ürün aileleri ile IT altyapınızı güçlendirin.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* UEM */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#E31C79] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-device-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Unified Endpoint Management (UEM)</h3>
                  <p className="text-gray-600 mt-2">Tüm uç noktalarınızı tek platformdan yönetin</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Masaüstü, dizüstü, mobil cihaz yönetimi
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Yazılım dağıtımı ve güncelleme
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Uzaktan destek ve kontrol
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  OS migration ve imaging
                </li>
              </ul>
              <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                Bu çözüm için danışmanlık alın
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* Patch Management */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#E31C79] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-bug-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Patch & Vulnerability Management</h3>
                  <p className="text-gray-600 mt-2">Güvenlik açıklarını proaktif olarak kapatın</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Otomatik patch tarama ve dağıtım
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Risk bazlı önceliklendirme
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  3. parti uygulama patch desteği
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Uyumluluk raporlama
                </li>
              </ul>
              <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                Bu çözüm için danışmanlık alın
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* ITSM */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#E31C79] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-2-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">ITSM – IT Servis Yönetimi</h3>
                  <p className="text-gray-600 mt-2">IT servis süreçlerinizi optimize edin</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Olay ve problem yönetimi
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Değişiklik ve sürüm yönetimi
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Self-servis portal
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  SLA yönetimi ve raporlama
                </li>
              </ul>
              <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                Bu çözüm için danışmanlık alın
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* ITAM */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#E31C79] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-archive-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">ITAM – Varlık Yönetimi</h3>
                  <p className="text-gray-600 mt-2">IT varlıklarınızı tam görünürlükle yönetin</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Donanım ve yazılım envanteri
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Lisans uyumluluk yönetimi
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Maliyet optimizasyonu
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Yaşam döngüsü yönetimi
                </li>
              </ul>
              <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                Bu çözüm için danışmanlık alın
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* DEX */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#E31C79] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-smile-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Digital Employee Experience (DEX)</h3>
                  <p className="text-gray-600 mt-2">Çalışan deneyimini ölçün ve iyileştirin</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Gerçek zamanlı deneyim skorları
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Proaktif sorun tespiti
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Otomatik iyileştirme aksiyonları
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Sentiment analizi
                </li>
              </ul>
              <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                Bu çözüm için danışmanlık alın
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* Security */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#E31C79] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Security & Access Management</h3>
                  <p className="text-gray-600 mt-2">Güvenli erişim ve kimlik yönetimi</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Zero Trust güvenlik modeli
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  VPN ve güvenli uzaktan erişim
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Mobil tehdit savunması
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <i className="ri-check-line text-[#E31C79]"></i>
                  Uygulama kontrolü
                </li>
              </ul>
              <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                Bu çözüm için danışmanlık alın
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* Supply Chain - Full Width */}
            <div className="lg:col-span-2 bg-gradient-to-r from-[#2B1B3D] to-[#3D2555] rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#FF6B35] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-truck-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Supply Chain (Depo & Lojistik Çözümleri)</h3>
                  <p className="text-gray-300 mt-2">Tedarik zinciri operasyonlarınızı optimize edin</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-300">
                    <i className="ri-check-line text-[#E31C79]"></i>
                    Depo yönetim sistemleri
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <i className="ri-check-line text-[#E31C79]"></i>
                    Sesli toplama çözümleri
                  </li>
                </ul>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-300">
                    <i className="ri-check-line text-[#E31C79]"></i>
                    Mobil barkod ve RFID
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <i className="ri-check-line text-[#E31C79]"></i>
                    Envanter optimizasyonu
                  </li>
                </ul>
              </div>
              <button onClick={() => scrollToSection('iletisim')} className="mt-6 text-[#FF6B35] font-semibold hover:text-[#E31C79] cursor-pointer flex items-center gap-2">
                Bu çözüm için danışmanlık alın
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="senaryolar" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Kullanım Senaryoları
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Gerçek dünya örnekleriyle Ivanti çözümlerinin işletmenize nasıl değer kattığını keşfedin.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Scenario 1 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/8af3d658a723439e2b50beb964621583.jpg"
                  alt="Ivanti patch yönetimi ile otomatik güncelleme"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Binlerce Cihaz için Otomatik Patch Yönetimi</h3>
                <p className="text-gray-600 mb-4">
                  Büyük ölçekli bir üretim firması, 5.000+ cihazda güvenlik yamalarını manuel olarak yönetmekte zorlanıyordu. Ivanti Patch Management ile tüm süreç otomatize edildi ve güvenlik açıkları %95 oranında azaltıldı.
                </p>
                <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                  Bu senaryoyu firmanızda uygulayabiliriz
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Scenario 2 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=IT%20service%20desk%20helpdesk%20modern%20office%20with%20multiple%20screens%20showing%20ticket%20management%20system%20professional%20support%20team%20working%20enterprise%20ITSM%20solution%20clean%20bright%20workspace&width=600&height=400&seq=scenario2&orientation=landscape"
                  alt="Ivanti ITSM ile hızlı servis talebi yönetimi"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">ITSM ile Servis Taleplerinin %40 Hızlı Kapanması</h3>
                <p className="text-gray-600 mb-4">
                  Bir finans kuruluşu, IT servis taleplerini e-posta ile yönetiyordu. Ivanti ITSM kurulumu sonrası self-servis portal ve otomasyon ile talep çözüm süresi %40 kısaldı.
                </p>
                <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                  Bu senaryoyu firmanızda uygulayabiliriz
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Scenario 3 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Software%20license%20management%20dashboard%20on%20computer%20screen%20showing%20compliance%20charts%20and%20asset%20inventory%20enterprise%20ITAM%20solution%20modern%20office%20professional%20environment&width=600&height=400&seq=scenario3&orientation=landscape"
                  alt="Ivanti ITAM ile lisans uyumluluk yönetimi"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">ITAM ile Lisans Uyumluluğunun Sağlanması</h3>
                <p className="text-gray-600 mb-4">
                  Perakende sektöründe faaliyet gösteren bir firma, yazılım lisans denetiminde ciddi risklerle karşı karşıyaydı. Ivanti ITAM ile tam envanter görünürlüğü sağlandı ve lisans maliyetleri %30 optimize edildi.
                </p>
                <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                  Bu senaryoyu firmanızda uygulayabiliriz
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            {/* Scenario 4 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Digital%20employee%20experience%20analytics%20dashboard%20showing%20user%20satisfaction%20metrics%20and%20performance%20graphs%20modern%20workplace%20technology%20DEX%20solution%20clean%20interface&width=600&height=400&seq=scenario4&orientation=landscape"
                  alt="Ivanti DEX ile dijital çalışan deneyimi optimizasyonu"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">DEX ile Kullanıcı Deneyimi Ölçümü ve Optimizasyonu</h3>
                <p className="text-gray-600 mb-4">
                  Enerji sektöründe bir firma, çalışanların IT sorunlarını proaktif olarak tespit edemiyordu. Ivanti DEX ile gerçek zamanlı deneyim skorları takip edildi ve çalışan memnuniyeti %25 arttı.
                </p>
                <button onClick={() => scrollToSection('iletisim')} className="text-[#E31C79] font-semibold hover:text-[#C01865] cursor-pointer flex items-center gap-2">
                  Bu senaryoyu firmanızda uygulayabiliriz
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section id="neden-biz" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Neden ivanti.tr?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Türkiye'de Ivanti çözümlerinde uzmanlaşmış teknik ekibimizle, kurumsal IT altyapınızı güçlendiriyoruz.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-xl text-[#E31C79]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      <a href="#iletisim" className="hover:text-[#E31C79]">Uzman Teknik Ekip</a>
                    </h4>
                    <p className="text-gray-600">Sertifikalı Ivanti uzmanlarından oluşan deneyimli kadro</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-xl text-[#E31C79]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      <a href="#iletisim" className="hover:text-[#E31C79]">Kurulum & Entegrasyon</a>
                    </h4>
                    <p className="text-gray-600">Uçtan uca proje yönetimi ve sorunsuz entegrasyon</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-graduation-cap-line text-xl text-[#E31C79]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      <a href="#iletisim" className="hover:text-[#E31C79]">Eğitim Hizmetleri</a>
                    </h4>
                    <p className="text-gray-600">Kullanıcı ve yönetici eğitimleri ile hızlı adaptasyon</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-headphone-line text-xl text-[#E31C79]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      <a href="#iletisim" className="hover:text-[#E31C79]">Yerel Dilde Destek</a>
                    </h4>
                    <p className="text-gray-600">Türkçe teknik destek ve hızlı yanıt süreleri</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-xl text-[#E31C79]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      <a href="#iletisim" className="hover:text-[#E31C79]">Lisans & Bakım</a>
                    </h4>
                    <p className="text-gray-600">Lisanslama danışmanlığı ve sürekli bakım hizmetleri</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-xl text-[#E31C79]"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      <a href="#iletisim" className="hover:text-[#E31C79]">Sektör Tecrübesi</a>
                    </h4>
                    <p className="text-gray-600">Enerji, Finans, Perakende, Üretim sektörlerinde kanıtlanmış başarı</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20IT%20consulting%20team%20meeting%20in%20modern%20office%20discussing%20enterprise%20technology%20solutions%20with%20laptops%20and%20presentation%20screen%20showing%20analytics%20corporate%20business%20environment&width=600&height=700&seq=whyus&orientation=portrait"
                alt="Ivanti Türkiye partner ekibi danışmanlık toplantısı"
                className="rounded-2xl shadow-2xl w-full"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#E31C79] text-white p-6 rounded-xl shadow-xl">
                <div className="text-4xl font-bold">10+</div>
                <div className="text-sm">Yıllık Tecrübe</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#2B1B3D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Müşterilerimiz Ne Diyor?
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Ivanti çözümlerini başarıyla uygulayan firmalardan geri bildirimler
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-[#3D2555] rounded-2xl p-8">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FF6B35]"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6 italic">
                "Patch yönetimini tamamen otomatik hale getirdiler. Artık güvenlik açıkları konusunda endişelenmiyoruz, sistem kendini güncel tutuyor."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#E31C79] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">AK</span>
                </div>
                <div>
                  <div className="text-white font-semibold">Ahmet K.</div>
                  <div className="text-gray-400 text-sm">IT Direktörü, Üretim Sektörü</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-[#3D2555] rounded-2xl p-8">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FF6B35]"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6 italic">
                "ITSM kurulumumuz 6 haftada sorunsuz tamamlandı. Ekip son derece profesyonel ve çözüm odaklı çalıştı."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#E31C79] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">SY</span>
                </div>
                <div>
                  <div className="text-white font-semibold">Selin Y.</div>
                  <div className="text-gray-400 text-sm">CIO, Finans Sektörü</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-[#3D2555] rounded-2xl p-8">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FF6B35]"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6 italic">
                "Envanter görünürlüğümüz %100'e yaklaştı. Lisans denetimlerinde artık hiçbir sürprizle karşılaşmıyoruz."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#E31C79] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">MÖ</span>
                </div>
                <div>
                  <div className="text-white font-semibold">Mehmet Ö.</div>
                  <div className="text-gray-400 text-sm">IT Müdürü, Perakende Sektörü</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="iletisim" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Hemen İletişime Geçin
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Ivanti çözümleri hakkında ücretsiz danışmanlık almak için formu doldurun. <strong>24 saat içinde size dönüş yapıyoruz.</strong>
              </p>
              
              <div className="bg-gray-50 rounded-2xl p-6 shadow-lg mb-8">
                <h3 className="font-semibold text-gray-900 mb-4">Size Sunduklarımız:</h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-700">
                    <i className="ri-check-double-line text-[#E31C79]"></i>
                    Ücretsiz ön analiz ve ihtiyaç değerlendirmesi
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <i className="ri-check-double-line text-[#E31C79]"></i>
                    Özelleştirilmiş çözüm planı
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <i className="ri-check-double-line text-[#E31C79]"></i>
                    Detaylı maliyet analizi
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <i className="ri-check-double-line text-[#E31C79]"></i>
                    Canlı demo imkanı
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 shadow-xl">
              <form 
                id="lead-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31C79] focus:border-[#E31C79] transition-colors text-sm"
                    placeholder="Adınız ve soyadınız"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Firma *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31C79] focus:border-[#E31C79] transition-colors text-sm"
                    placeholder="Firma adınız"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31C79] focus:border-[#E31C79] transition-colors text-sm"
                      placeholder="ornek@firma.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31C79] focus:border-[#E31C79] transition-colors text-sm"
                      placeholder="+90 5XX XXX XX XX"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="interest" className="block text-sm font-medium text-gray-700 mb-2">
                    İlgi Alanı *
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        const dropdown = document.getElementById('interest-dropdown');
                        if (dropdown) {
                          dropdown.classList.toggle('hidden');
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31C79] focus:border-[#E31C79] transition-colors text-sm text-left bg-white flex items-center justify-between cursor-pointer"
                    >
                      <span className={formData.interest ? 'text-gray-900' : 'text-gray-500'}>
                        {formData.interest || 'Çözüm seçiniz'}
                      </span>
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </button>
                    <div id="interest-dropdown" className="hidden absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      {['UEM - Uç Nokta Yönetimi', 'Patch Management', 'ITSM - Servis Yönetimi', 'ITAM - Varlık Yönetimi', 'DEX - Çalışan Deneyimi', 'Security - Güvenlik', 'Supply Chain - Lojistik', 'Genel Danışmanlık'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, interest: option }));
                            const dropdown = document.getElementById('interest-dropdown');
                            if (dropdown) {
                              dropdown.classList.add('hidden');
                            }
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-[#E31C79]/10 hover:text-[#E31C79] cursor-pointer"
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Mesajınız
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31C79] focus:border-[#E31C79] transition-colors text-sm resize-none"
                    placeholder="Projeniz hakkında kısa bilgi verin..."
                  ></textarea>
                  <div className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#E31C79] hover:bg-[#C01865] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Gönderiliyor...
                    </span>
                  ) : (
                    'Demo Talep Et'
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <i className="ri-check-line"></i>
                    Talebiniz başarıyla alındı. En kısa sürede size dönüş yapacağız.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    Bir hata oluştu. Lütfen tekrar deneyin.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2B1B3D] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="text-2xl font-bold mb-4">
                <span className="font-pacifico" style={{ fontFamily: '"Pacifico", serif' }}>ivanti</span>
                <span className="text-[#E31C79]">.tr</span>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Türkiye'de Ivanti çözümlerinin yetkili entegratörü olarak, kurumsal IT altyapınızı güçlendirmek için lisans, kurulum, danışmanlık ve destek hizmetleri sunuyoruz.
              </p>
              <div className="flex items-center gap-3">
                <a href="#" className="w-10 h-10 bg-[#3D2555] rounded-lg flex items-center justify-center hover:bg-[#E31C79] transition-colors cursor-pointer" rel="nofollow">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-[#3D2555] rounded-lg flex items-center justify-center hover:bg-[#E31C79] transition-colors cursor-pointer" rel="nofollow">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-[#3D2555] rounded-lg flex items-center justify-center hover:bg-[#E31C79] transition-colors cursor-pointer" rel="nofollow">
                  <i className="ri-youtube-fill text-lg"></i>
                </a>
              </div>
            </div>

            {/* Quick Links - Çözümler */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Çözümler</h4>
              <ul className="space-y-2">
                <li><a href="#cozumler" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">UEM</a></li>
                <li><a href="#cozumler" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">Patch Management</a></li>
                <li><a href="#cozumler" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">ITSM</a></li>
                <li><a href="#cozumler" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">ITAM</a></li>
                <li><a href="#cozumler" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">DEX</a></li>
              </ul>
            </div>

            {/* Quick Links - Kurumsal */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Kurumsal</h4>
              <ul className="space-y-2">
                <li><a href="#neden-biz" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">Hakkımızda</a></li>
                <li><a href="#senaryolar" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">Referanslar</a></li>
                <li><a href="#iletisim" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">İletişim</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">Blog</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#3D2555] pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
              <div className="text-gray-400">
                © 2024 ivanti.tr - Tüm hakları saklıdır.
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
                <a href="#" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">Gizlilik Politikası</a>
                <a href="#" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">KVKK</a>
                <a href="#" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors">Çerez Politikası</a>
                <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-[#FF6B35] cursor-pointer transition-colors" rel="nofollow">Powered by WOTECH</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
